import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useMe } from '../lib/me';
import Modal from './Modal';

/**
 * 마감일 공지 팝업(2026-11-04 way) — 「마감 전에 공지팝업 · 마감일 13시·15시 두 번 · 작업을 서둘러 달라」.
 *
 * 원천 = `close_deadline`(listCloseDeadlines). 기한 **당일**(KST) 13:00·15:00 이 지나면 뜬다.
 * 두 시각이 다 지난 뒤 처음 열었으면 한 번만(「확인」은 지나간 시각을 전부 확인 처리).
 * 받는 사람 = 그 기한에 막히는 사람:
 *   영업   = 관리자·회계 외 전원(서버 assertDeadlineOpen 과 같은 면제)
 *   월정기 = 관리자·회계 외 + 월정기 화면 입력 권한(인차팀)
 *   회계   = 회계(accounting) 역할 — 막히지는 않고 알림만
 * 「확인」 기억 = 이 브라우저(localStorage). 다른 PC 에선 한 번 더 뜰 수 있다(무해).
 */

const SLOTS = [13, 15] as const;
const ACK_KEY = 'close-deadline-ack-v1';
const FETCH_MS = 10 * 60_000;
const TICK_MS = 30_000;

export type Row = { driveMon: string; kind: string; deadline: string; closed: boolean };

function kstNow(): { day: string; hour: number } {
  const d = new Date(Date.now() + 9 * 3600_000);
  return { day: d.toISOString().slice(0, 10), hour: d.getUTCHours() };
}
function readAck(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(ACK_KEY) ?? '[]') as string[]); } catch { return new Set(); }
}
function writeAck(keys: string[]) {
  try {
    const s = readAck();
    keys.forEach((k) => s.add(k));
    localStorage.setItem(ACK_KEY, JSON.stringify([...s].slice(-60)));
  } catch { /* 못 쓰면 다음 박자에 다시 뜬다 */ }
}

const AREA: Record<string, string> = {
  영업: '배차·배정, 통근 운행일수, 접수·노선, 영업정산',
  월정기: '월정기 등록·취소·수정',
};

function message(r: Row): { title: string; body: string } {
  const [, m] = r.driveMon.split('-').map(Number);
  const [, dm, dd] = r.deadline.split('-').map(Number);
  if (r.kind === '회계') {
    return {
      title: `오늘은 ${m}월 회계 마감일입니다`,
      body: `오늘(${dm}월 ${dd}일)이 ${m}월 회계 마감일입니다. 남은 마감 작업을 서둘러 마무리해 주세요.`,
    };
  }
  const area = AREA[r.kind] ?? r.kind;
  return {
    title: `오늘은 ${m}월 ${r.kind === '영업' ? '영업 정산' : r.kind} 마감일입니다`,
    body: `오늘(${dm}월 ${dd}일)이 ${m}월 ${r.kind === '영업' ? '영업 정산' : r.kind} 마감일입니다. 오늘 밤 12시가 지나면 ${m}월분 ${area}을 더 이상 고칠 수 없습니다. 남은 작업을 서둘러 마무리해 주세요.`,
  };
}

export function CloseDeadlineAlert() {
  const { me } = useMe();
  const rows = useRef<Row[]>([]);
  const [pop, setPop] = useState<{ row: Row; keys: string[] }[] | null>(null);

  useEffect(() => {
    if (!me) return;
    const exempt = me.role === 'admin' || me.role === 'accounting';
    const mine = (r: Row) => {
      if (r.kind === '영업') return !exempt;
      if (r.kind === '월정기') return !exempt && me.screens?.['result-mo'] === 'write';
      if (r.kind === '회계') return me.role === 'accounting';
      return false;
    };
    let alive = true;
    const check = () => {
      const { day, hour } = kstNow();
      const ack = readAck();
      const due: { row: Row; keys: string[] }[] = [];
      for (const r of rows.current) {
        if (r.deadline !== day || !mine(r)) continue;
        const keys = SLOTS.filter((h) => hour >= h).map((h) => `${r.driveMon}|${r.kind}|${r.deadline}|${h}`);
        if (keys.some((k) => !ack.has(k))) due.push({ row: r, keys });
      }
      setPop(due.length ? due : null);
    };
    const load = () => {
      api.listCloseDeadlines({}).then((res) => {
        if (!alive) return;
        rows.current = res.rows as Row[];
        check();
      }).catch(() => { /* 알림은 없어도 업무 화면은 그대로 */ });
    };
    load();
    const f = setInterval(load, FETCH_MS);
    const t = setInterval(check, TICK_MS);
    window.addEventListener('focus', check);
    return () => { alive = false; clearInterval(f); clearInterval(t); window.removeEventListener('focus', check); };
  }, [me]);

  if (!pop) return null;
  const close = () => { writeAck(pop.flatMap((p) => p.keys)); setPop(null); };
  return <DeadlineNotice rows={pop.map((p) => p.row)} onClose={close} />;
}

const WHO: Record<string, string> = {
  영업: '관리자·회계 외 전원',
  월정기: '관리자·회계 외 · 월정기 입력 권한',
  회계: '회계 담당',
};

/**
 * 영업 마감 팝업 아래 「내 접수 중 청구 요청 전」(11-05 way · 서버 `billNudge.ts listMyUnbilled` — DM 과 같은 판정).
 * 기한 뒤 청구분은 마감 후 지급 대기(승인)로 가므로 기한 전에 올리게 한다. 없으면 아무것도 안 그린다(미리보기 = 관리자 본인 담당 기준).
 */
function MyUnbilled({ mon }: { mon: string }) {
  const [rows, setRows] = useState<{ recvId: number; recvType: string; custName: string | null; pay: string }[] | null>(null);
  useEffect(() => {
    let alive = true;
    api.listMyUnbilled({ mon }).then((r) => { if (alive) setRows(r.rows); }).catch(() => { /* 없어도 팝업은 그대로 */ });
    return () => { alive = false; };
  }, [mon]);
  if (!rows?.length) return null;
  const won = (s: string) => Math.round(Number(s)).toLocaleString('ko-KR');
  return (
    <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-sm">
      <div className="font-semibold">내 접수 중 청구 요청 전 {rows.length}건</div>
      <ul className="mt-1 max-h-40 space-y-0.5 overflow-auto text-xs">
        {rows.map((r) => (
          <li key={r.recvId}>접수 {r.recvId} {r.custName ?? ''}{r.recvType === '통근' ? '(통근)' : ''} — 기사 지급 {won(r.pay)}원</li>
        ))}
      </ul>
      <p className="mt-1 text-xs text-ink-soft">기한이 지난 뒤 청구 요청하면 기사 지급이 「마감 후 지급 대기」로 넘어가 승인을 받아야 그 달 명세에 들어갑니다.</p>
    </div>
  );
}

/** 팝업 본체 — 실제 알림과 관리자 「팝업 미리보기」(preview) 가 같은 화면을 쓴다(베끼면 갈린다). */
export function DeadlineNotice({ rows, onClose, preview }: { rows: Row[]; onClose: () => void; preview?: boolean }) {
  return (
    <Modal width="max-w-lg">
      <div className="card space-y-3 p-5">
        {preview && (
          <div className="rounded bg-slate-100 px-2 py-1 text-xs text-ink-soft">
            미리보기 — 기한 당일 오후 1시·3시에 받는 분들께 이렇게 뜹니다.
          </div>
        )}
        {rows.map((row) => {
          const msg = message(row);
          return (
            <div key={`${row.driveMon}|${row.kind}`} className="space-y-1">
              <div className="text-lg font-bold">⏰ {msg.title}</div>
              <p className="text-sm text-ink-soft">{msg.body}</p>
              {row.kind === '영업' && <MyUnbilled mon={row.driveMon} />}
              {preview && <p className="text-xs text-ink-faint">받는 분: {WHO[row.kind] ?? '—'}</p>}
            </div>
          );
        })}
        <div className="flex justify-end">
          <button className="btn-primary" onClick={onClose}>확인</button>
        </div>
      </div>
    </Modal>
  );
}
